import { Button, Space, Tag, Typography } from 'antd'
import { FireOutlined } from '@ant-design/icons'
import { Link } from 'react-router-dom'

import { Hero } from '../landing/hero'
import { Counts } from '../landing/counts'
import {
  AgainstPprof,
  ProfileTypes,
  RegressionExample,
  Section,
  TheProblem,
} from '../landing/sections'
import { AlongsideYourStack, ClosingCTA, CostAngle, PipelineGate, Trust } from '../landing/rest'
import { useDocumentTitle } from '../seo/useDocumentTitle'

/**
 * The homepage.
 *
 * Ordered by the messaging hierarchy rather than by the architecture: the
 * outcome first, the mechanism after, the technology last. Somebody who wants
 * to read about pprof will get there; somebody who wants to know why their bill
 * went up should not have to pass through flame graphs to find out.
 *
 * Every capability shown is one the platform has today. There is no "coming
 * soon" band, because the page is read by people deciding whether to trust us
 * with a production process, and the fastest way to lose that is to be caught
 * describing something that does not exist. What we cannot do has its own
 * section instead, near the bottom, where it is easy to find and impossible to
 * mistake for a feature.
 */
export function LandingPage() {
  useDocumentTitle(
    'Find Go performance regressions between deploys | PecGoProf',
    'PecGoProf profiles Go services continuously, compares each build with the one it replaced, ' +
      'and reports which function started costing more. Go CPU, memory, allocation and goroutine ' +
      'profiling with flame graphs and deploy comparison.',
  )

  return (
    <div style={{ background: '#fff' }}>
      <Hero />
      <Counts />

      <Section
        title="What a regression looks like"
        lead="This is the message, not a mock-up of one: a service, the two builds either side of the boundary, and the functions that took a larger share of the profile."
      >
        <RegressionExample />
      </Section>

      <Section title="The afternoon this replaces" mascot="detective">
        <TheProblem />
      </Section>

      <Section title="pprof shows a profile. PecGoProf shows what changed." dark={false}>
        <AgainstPprof />
      </Section>

      <Section
        title="Built for Go, and only for Go"
        lead="The parser, the analysis and the SDK all assume Go runtime semantics. That assumption is what lets a finding say something specific instead of something that would be true of any language."
        dark
      >
        <ProfileTypes />
      </Section>

      <PipelineGate />
      <CostAngle />
      <AlongsideYourStack />
      <Trust />
      <Limitations />
      <ClosingCTA />
      <Footer />
    </div>
  )
}

/**
 * What it does not do.
 *
 * Kept from the first version of this page, and still the most valuable section
 * on it: it is the reason a reader believes the rest. Updated where the product
 * moved — the SDK captures on its own schedule now, so the old line about not
 * profiling continuously has stopped being true and has been replaced with
 * limits that are.
 */
function Limitations() {
  return (
    <div style={{ padding: '72px 24px', background: '#fafafa' }}>
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <Typography.Title level={3} style={{ marginTop: 0, marginBottom: 20 }}>
          What it does not do
        </Typography.Title>

        <Space direction="vertical" size={16}>
          <Typography.Paragraph style={{ marginBottom: 0 }}>
            <Typography.Text strong>It does not tell you what caused anything.</Typography.Text> A
            profile shows correlation. Findings point at measurements and use words like "suggests"
            and "worth investigating", because anything stronger would be a lie with a progress bar.
          </Typography.Paragraph>

          <Typography.Paragraph style={{ marginBottom: 0 }}>
            <Typography.Text strong>It needs your build version.</Typography.Text> Deploy
            comparison is comparison across a boundary, and without a version there is no boundary.
            One line of SDK config, and the service page says so when a service is missing it.
          </Typography.Paragraph>

          <Typography.Paragraph style={{ marginBottom: 0 }}>
            <Typography.Text strong>It only understands Go.</Typography.Text> Deliberately, and it
            is not on the way to understanding anything else.
          </Typography.Paragraph>

          <Typography.Paragraph style={{ marginBottom: 0 }}>
            <Typography.Text strong>There is no self-hosted build.</Typography.Text> If your data
            genuinely cannot leave your network, say so and we will tell you honestly where that
            stands rather than sell you a plan.
          </Typography.Paragraph>
        </Space>
      </div>
    </div>
  )
}

function Footer() {
  return (
    <div style={{ padding: '56px 24px', background: '#0f1419', color: 'rgba(255,255,255,0.6)' }}>
      <div
        style={{
          maxWidth: 1080,
          margin: '0 auto',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 24,
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Space direction="vertical" size={4}>
          <Space size={10}>
            <FireOutlined style={{ color: '#fa8c16' }} />
            <Typography.Text style={{ color: '#fff' }}>PecGoProf</Typography.Text>
            <Tag color="blue">beta</Tag>
          </Space>
          <Typography.Text style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>
            Go performance regressions, found between deploys.
          </Typography.Text>
        </Space>

        <Space size="large" wrap>
          {[
            ['/demo', 'Demo'],
            ['/pricing', 'Pricing'],
            ['/terms', 'Terms'],
            ['/privacy', 'Privacy'],
            ['/refunds', 'Refunds'],
            ['/login', 'Sign in'],
          ].map(([to, label]) => (
            <Link key={to} to={to}>
              <Typography.Text style={{ color: 'rgba(255,255,255,0.6)' }}>{label}</Typography.Text>
            </Link>
          ))}
          <Link to="/login?tab=register&plan=developer">
            <Button type="primary" size="small">
              Start trial
            </Button>
          </Link>
        </Space>
      </div>
    </div>
  )
}
